import { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import Select, { GroupBase, OptionsOrGroups, PropsValue } from "react-select";
import DatePicker from "react-datepicker";
import { offset, shift, limitShift } from "@floating-ui/react";

import StatsReports from "../statsReports";
import { StatisticsPropsType, StatisticsSearchProps } from "./types";
import { format, parse } from "date-fns";

const timeData = [
  { value: "day", label: "Group by Day" },
  { value: "week", label: "Group by Week" },
  { value: "month", label: "Group by Month" },
  { value: "quarter", label: "Group by Quarter" },
];
const comparison = [
  { value: "p-1", label: "Compare with previous Period" },
  { value: "y-1", label: "Compare with Previous Year" },
]


const CaseFilter: FC<StatisticsPropsType> = ({
  updateQueryParams,
  defaultQueryParams,
  title
}): JSX.Element => {

  const { control, handleSubmit, getValues, watch } = useForm<StatisticsSearchProps>({
    mode: "all",
    defaultValues: defaultQueryParams,
  });

  const start_date = watch("start_date");
  const end_date = watch("end_date");

  const searchForStats = (values: StatisticsSearchProps) => {
    const updatedValues = {
      ...values,
      period: values.period
        ? typeof values.period === "string"
          ? values.period
          : (values.period as unknown as { value: string; label: string }).value
        : "day",
      // soc_value:
      //   values.soc_value ?
      //     (typeof values.soc_value === "string" ? values.soc_value : (values.soc_value as unknown as { value: string; name: string; }).value)
      //     : "",
      // drug_id:
      //   values.drug_id ?
      //     (typeof values.drug_id === "string" ? values.drug_id : (values.drug_id as unknown as { value: string; name: string; }).value)
      //     : "",
      filter_type: values.filter_type
        ? typeof values.filter_type === "string"
          ? values.filter_type
          : (values.filter_type as unknown as { value: string; label: string }).value
        : "",
      user_id:
        values.user_id ?
          (typeof values.user_id === "string" ? values.user_id : (values.user_id as unknown as { value: string; name: string; }).value)
          : "",
    };

    updateQueryParams(updatedValues, false);
  };

  return (
    <form onSubmit={handleSubmit(searchForStats)}>
      <div className="card">
        <div className="card-header"><h4 className="m-0">{title ? title : 'Statistics'}</h4></div>

        <div className="card-body pt-3">
          <div className="row g-3 statistics-filter mb-3">
            <div className="col">
              <div className={"row"}>
                <label className={"form-label col-lg-4 col-md-4 col-12"}>Start date</label>
                <div className={"col-lg-8 col-md-8 col-12"}>
                  <div className="position-relative">
                    <Controller
                      name="start_date"
                      control={control}
                      defaultValue=""
                      render={({field}) => (
                        <DatePicker
                          selected={field.value.length > 0 ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                          onChange={(date) => date ? field.onChange(format(date, "yyyy-MM-dd")) : ""}
                          className="form-control form-control-datepicker rounded-1"
                          wrapperClassName="w-100"
                          placeholderText="mm/dd/yyyy"
                          dateFormat="MM/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          maxDate={end_date ? new Date(end_date) : undefined}
                          isClearable={false}
                          portalId="root"
                          popperModifiers={[offset(8), shift({limiter: limitShift()})]}
                        />
                      )}
                    />
                    <i
                      className="bi bi-calendar position-absolute"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="col">
              <div className={"row"}>
                <label className={"form-label col-lg-4 col-md-4 col-12"}>End date</label>
                <div className={"col-lg-8 col-md-8 col-12"}>
                  <div className="position-relative">
                    <Controller
                      name="end_date"
                      control={control}
                      defaultValue=""
                      render={({field}) => (
                        <DatePicker
                          selected={field.value.length > 0 ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                          onChange={(date) => date ? field.onChange(format(date, "yyyy-MM-dd")) : ""}
                          className="form-control form-control-datepicker rounded-1"
                          wrapperClassName="w-100"
                          placeholderText="mm/dd/yyyy"
                          dateFormat="MM/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          minDate={start_date ? new Date(start_date) : undefined}
                          isClearable={false}
                          portalId="root"
                          popperModifiers={[offset(8), shift({limiter: limitShift()})]}
                        />
                      )}
                    />
                    <i
                      className="bi bi-calendar position-absolute"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    ></i>
                  </div>
              </div>
            </div>
          </div>
            <div className="col min-col">
              <Controller
                name="period"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    isClearable={true}
                    placeholder={"Group Report By..."}
                    options={
                      (timeData && timeData.length > 0 ?
                        timeData.map((item) => ({
                          value: item.value,
                          label: item.label,
                        }))
                        : []
                      ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                    }
                    id="time"
                    {...field}
                    value={
                      (field.value ?
                        timeData.find((time) =>  time.value === field.value)
                        : null
                      ) as unknown as PropsValue<string>
                    }
                  />
                )}
              />
            </div>
            <div className="col min-col">
              <Controller
                name="filter_type"
                control={control}
                defaultValue=""
                render={({field}) => (
                  <Select
                    isClearable={true}
                    placeholder={"Compare With ..."}
                    options={
                      (comparison && comparison.length > 0
                        ? comparison.map((item) => ({
                          value: item.value,
                          label: item.label,
                        }))
                        : []
                      ) as unknown as
                        OptionsOrGroups<string, GroupBase<string>>
                    }
                    id="time"
                    {...field}
                  />
                )}
              />
            </div>
            <div className="col">
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </button>
            </div>
          </div>
          <StatsReports getValues={getValues}/>
        </div>
      </div>
    </form>
  );
};

export default CaseFilter;
